import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Ticket } from 'lucide-react'
import { api } from '../../api/client'
import { Button } from '../ui/Button'
import { usePaywallStore } from '../../store/paywall.store'
import { PromoCodeModal } from '../promo/PromoCodeModal'

/**
 * Premium status block at the top of /profile. Shows:
 *   - Premium with period-end date when active (with provider note —
 *     'оплачено' for paid sub, 'промо' for promo-granted, 'free-period'
 *     for platform fallback).
 *   - "Free" card + Subscribe CTA when not active.
 *
 * Auto-refreshes every 5 min so a fresh redeem reflects without a hard
 * reload, but doesn't poll aggressively.
 */
export function PremiumStatusCard() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language === 'en' ? 'en' : 'ru'
  const showPaywall = usePaywallStore((s) => s.show)
  const [promoOpen, setPromoOpen] = useState(false)

  const q = useQuery({
    queryKey: ['entitlements', 'me'],
    queryFn: () => api.entitlementsMe(),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  })

  // While loading, render nothing — keeps the profile layout from popping
  // on each navigation. q.error is unlikely (auth would already have
  // bounced) so we just no-op.
  if (!q.data) return null

  const d = q.data

  if (!d.isPremium) {
    return (
      <section className="flex flex-col gap-2 rounded-3xl bg-white/60 p-5 ring-1 ring-sage-200/60">
        <div className="flex items-center gap-2">
          <img
            src="/icons/premium.png"
            alt=""
            aria-hidden="true"
            className="h-6 w-6 shrink-0 opacity-60 grayscale select-none"
            draggable={false}
          />
          <h3 className="font-serif text-lg text-sage-800">{t('premium.free_title')}</h3>
        </div>
        <p className="text-sm text-sage-600">{t('premium.free_subtitle')}</p>
        <Button
          size="md"
          variant="primary"
          className="mt-1"
          onClick={() => showPaywall({ reason: 'manual' })}
        >
          {t('premium.subscribe_cta')}
        </Button>
        <button
          type="button"
          onClick={() => setPromoOpen(true)}
          className="mt-1 inline-flex items-center justify-center gap-1 text-[11px] text-sage-600 underline-offset-2 hover:text-sage-800 hover:underline"
        >
          <Ticket className="h-3 w-3" strokeWidth={1.6} aria-hidden />
          {t('paywall.have_promo')}
        </button>
        <PromoCodeModal open={promoOpen} onClose={() => setPromoOpen(false)} />
      </section>
    )
  }

  const untilLabel = d.currentPeriodEnd
    ? new Date(d.currentPeriodEnd).toLocaleDateString(lang === 'en' ? 'en-GB' : 'ru-RU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null

  const providerLabel =
    d.provider === 'promo'
      ? t('premium.via_promo')
      : d.provider === 'platform_free_period'
        ? t('premium.via_free_period')
        : t('premium.via_paid')

  return (
    <section className="flex flex-col gap-1 rounded-3xl bg-gradient-to-br from-[#fdf3e3] to-[#fbeadd] p-5 ring-1 ring-[#e6c4a4]/60">
      <div className="flex items-center gap-2">
        <img
          src="/icons/premium.png"
          alt=""
          aria-hidden="true"
          className="h-7 w-7 shrink-0 select-none"
          draggable={false}
        />
        <h3 className="font-serif text-lg text-sage-800">{t('premium.active_title')}</h3>
        <span className="rounded-full bg-copper/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-copper">
          premium
        </span>
      </div>
      {untilLabel ? (
        <p className="text-sm text-sage-700">
          {t('premium.active_until', { date: untilLabel })}
        </p>
      ) : (
        <p className="text-sm text-sage-700">{t('premium.active_no_expiry')}</p>
      )}
      <p className="text-xs text-sage-500">
        {providerLabel}
        {d.autoRenew && d.provider !== 'promo' ? ` · ${t('premium.auto_renew')}` : ''}
      </p>
      <button
        type="button"
        onClick={() => setPromoOpen(true)}
        className="mt-1 inline-flex w-fit items-center gap-1 text-[11px] text-sage-600 underline-offset-2 hover:text-sage-800 hover:underline"
      >
        <Ticket className="h-3 w-3" strokeWidth={1.6} aria-hidden />
        {t('paywall.have_promo')}
      </button>
      <PromoCodeModal open={promoOpen} onClose={() => setPromoOpen(false)} />
    </section>
  )
}
